import { useEffect } from 'react';
import { ApiError, api } from '../api/client.ts';
import { apiOrigin, isNative, nativePlatform, sessionToken } from '../api/origin.ts';
import {
  CONSENT_VERSION,
  adoptGalleryDeviceId,
  confirmDeviceRegistration,
  deviceRegistrationConfirmed,
  galleryPlugin,
  remintGalleryDeviceId,
  shouldStartSync,
  withTimeout,
  type GalleryPlugin,
} from './gallery.ts';
import { galleryLog } from './galleryDebug.ts';

/**
 * Starts the camera-roll sync on an ordinary app open, without anybody asking.
 *
 * This is what makes the feature a backup rather than a button. The engine's
 * state — the index of what has gone, the origin, the token, whether a run is
 * going — lives in a `GallerySync` that is rebuilt from nothing on every launch,
 * and until this existed the only thing that ever configured and started it was
 * the «Разрешить доступ» press on the consent screen. That press happens once,
 * ever: the button is hidden the moment the access is `full`. So every launch
 * after the first left an engine that had been told nothing, reporting «0 из 0,
 * проход не идёт» perfectly accurately, never enumerating the library and — the
 * symptom that is impossible to guess from the outside — never asking
 * `POST /api/gallery/wanted`, so a photograph marked `original_requested = 1`
 * was never collected however many times the app was restarted.
 *
 * Both halves of the token problem are solved by doing it here rather than in
 * Swift on launch: the session id is the web layer's, and handing it over again
 * on every open is also how an engine holding an expired one is repaired. See
 * `configure` in GallerySync.swift for why it needs its own copy at all.
 *
 * `enabled` is the consent gate and it comes from the engine's UserDefaults, so
 * this can never switch the feature on by itself — it only resumes what was
 * agreed to on the screen.
 */

/**
 * A status/configure/start is a dictionary lookup and two assignments on the
 * other side; generous by the standards of that, and short enough that a
 * launch on a broken build says so rather than hanging.
 */
const CALL_TIMEOUT_MS = 5000;

export function useGallerySync(userId: string | undefined): void {
  useEffect(() => {
    if (!isNative || !userId) return;
    // Declared after the guard, so it is a `string` rather than a narrowed
    // `string | undefined` — narrowing does not reach into the closure below.
    const owner = userId;
    let disposed = false;

    async function kick(reason: string): Promise<void> {
      const plugin: GalleryPlugin | null = galleryPlugin();
      if (!plugin) return;
      try {
        const status = await withTimeout(plugin.status(), CALL_TIMEOUT_MS, 'status');
        if (disposed) return;
        if (!shouldStartSync(status)) {
          // The silent case, said out loud — and the reason with it. An app
          // that opens, decides not to sync and says nothing is
          // indistinguishable from one that never ran this at all, which is
          // half of «телефон просто перестал загружать». Each of the three is
          // a different repair: agree on the screen, grant full access, or
          // wait because it is already going.
          galleryLog(
            `— проход не нужен: доступ «${status.permission}», согласие ` +
              `${status.enabled ? 'есть' : 'нет'}, ` +
              `${status.running ? 'проход уже идёт' : 'проход не идёт'} —`,
            'auto.skip',
          );
          return;
        }

        // Written to the panel because the panel is the only window into this
        // on a TestFlight phone, and «did it even try» is the first question
        // asked of it.
        galleryLog(`— ${reason} —`, 'auto');

        // The registration half of the resume, which used to belong to the
        // consent screen alone — and the screen is visited once, ever. So an
        // install whose id the server had stopped recognising (the id wiped
        // with the WebView's storage, or minted under an account that has
        // since signed out) had no path back: every batch answered 404
        // «Устройство не зарегистрировано» until somebody happened to open
        // that screen, which on a phone that was set up months ago is never.
        // Confirming here, before every start, closes all of those at the
        // price of one upsert per relogin or wipe — the ordinary launch pays
        // nothing, deviceRegistrationConfirmed short-circuits it.
        //
        // This never *grants* anything: shouldStartSync above has already
        // required the engine's own consent flag, so what is re-recorded is an
        // agreement the screen took. The screen stays the only place a person
        // says yes.
        let deviceId = adoptGalleryDeviceId(status.deviceId);
        if (!deviceRegistrationConfirmed(owner, deviceId)) {
          try {
            await api.registerGalleryDevice(deviceId, CONSENT_VERSION, nativePlatform());
            confirmDeviceRegistration(owner, deviceId);
          } catch (error) {
            if (error instanceof ApiError && error.status === 409) {
              // The consent screen's repair, verbatim: the stored id belongs
              // to an account that used this phone before. One mint and one
              // retry, not a loop — a second 409 on a fresh UUID is a
              // different problem and deserves to be seen.
              galleryLog(
                'это устройство записано за другим аккаунтом — заводим новый идентификатор',
                'auto.remint',
              );
              deviceId = remintGalleryDeviceId();
              await api.registerGalleryDevice(deviceId, CONSENT_VERSION, nativePlatform());
              confirmDeviceRegistration(owner, deviceId);
            } else {
              // A server that cannot be reached will fail the run in its first
              // batch anyway, and a registered device loses nothing by
              // starting unconfirmed — so say so and go on rather than
              // trading a whole pass for one flaky request.
              galleryLog(
                `✕ регистрация устройства: ${error instanceof Error ? error.message : String(error)}`,
                'auto.register',
              );
            }
          }
        }

        await withTimeout(
          plugin.configure({
            origin: apiOrigin,
            token: sessionToken() ?? '',
            deviceId,
            userId: owner,
          }),
          CALL_TIMEOUT_MS,
          'configure',
        );
        if (disposed) return;
        await withTimeout(plugin.start({ source: 'auto' }), CALL_TIMEOUT_MS, 'start');
      } catch (error) {
        galleryLog(
          `✕ автозапуск: ${error instanceof Error ? error.message : String(error)}`,
          'auto.fail',
        );
      }
    }

    void kick('приложение открыто, продолжаем синхронизацию');

    // And again on the way back from the background, because the WebView
    // survives that while a run does not: iOS suspends the app, `start`'s grace
    // window expires and the loop is cut, and without this the library would
    // only move on cold starts.
    let handle: { remove: () => Promise<void> } | null = null;
    void import('@capacitor/app').then(async ({ App }) => {
      const listener = await App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) void kick('приложение снова открыто, продолжаем синхронизацию');
      });
      if (disposed) void listener.remove();
      else handle = listener;
    });

    return () => {
      disposed = true;
      void handle?.remove();
    };
  }, [userId]);
}
